// 매장찾기 공통 유틸 (매장 데이터 변환 · 거리 · 검색 · 지도 라벨)

// 위치 정보가 없을 때 지도 기본 위치 (다비치 히어링 본사)
export const HQ = {
  lat: 37.5693842953837,
  lng: 126.989299276542,
  label: "다비치 히어링 보청기 본사",
};

export const MARKER_ICON = {
  default: "https://img.davich.com/image/sub/support/map/icon-sub-marker.png",
  active: "https://img.davich.com/image/sub/support/map/icon-main-marker.png",
};

// 지도 확대 범위 (전국 ~ 골목)
// labelMin 보다 멀리 보면: 선택 매장 외 이름은 숨기고, 가까이 붙은 핀은 숫자 동그라미로 묶는다
export const MAP_ZOOM = { min: 7, max: 19, initial: 17, labelMin: 14, myLocationMax: 16 };

// 묶음(클러스터) 기준: 화면에서 이 픽셀 칸 안에 들어오는 핀끼리 묶는다
export const CLUSTER_CELL_PX = 64;

// 마커를 "눈에 보이는 가운데"에 두기 위한 크기 (라벨+핀 전체가 기준점 위아래로 차지하는 px)
// 매장: 기준점 = 핀 끝. PC 라벨(46px)+핀(45px), 모바일 라벨(30px)+핀(45px) / 멀리서 볼 때(라벨 숨김)는 핀(45px)만
// 내 위치: 기준점 = 파란 점 가운데. 라벨(28px)+간격+점(14px)
export const MARKER_BLOCK = {
  store: { desktop: { above: 89, below: 0 }, mobile: { above: 79, below: 0 } },
  storePin: { desktop: { above: 45, below: 0 }, mobile: { above: 45, below: 0 } },
  myLocation: { desktop: { above: 37, below: 7 }, mobile: { above: 37, below: 7 } },
};

// 지도 아래쪽에서 가운데 계산에서 빼는 높이 (네이버 로고·저작권 / 큰 "내 위치" 버튼까지)
export const MAP_BOTTOM_RESERVE = {
  desktop: { normal: 22, withLargeButton: 68 },
  mobile: { normal: 22, withLargeButton: 64 },
};

export function isMobileView() {
  return window.matchMedia("(max-width: 768px)").matches;
}

// 매장 마커 아이콘: 기준점(anchor)을 핀 끝에 맞춰 실제 위치를 정확히 가리키게 한다
// (예전에는 기준점이 없어 핀 끝이 실제 위치보다 20px 아래에 찍혔음)
export function storeMarkerIcon(nm, label, active, highlighted = false) {
  return {
    content: markerHtml(label, active, highlighted),
    size: new nm.Size(45, 45),
    anchor: new nm.Point(22.5, 65),
  };
}

// 멀리서 볼 때 묶음 표시 (기존 파란색 톤, 숫자가 클수록 조금 크게)
export function clusterIcon(nm, count) {
  const size = count < 10 ? 40 : count < 30 ? 48 : 56;
  return {
    content: `<div class="store-cluster" style="width:${size}px;height:${size}px;line-height:${size - 6}px">${count}</div>`,
    size: new nm.Size(size, size),
    anchor: new nm.Point(size / 2, size / 2),
  };
}

// 검색할 때만 쓰는 시·도 표기 통일표.
// 매장 주소 데이터에 "서울 / 서울시 / 서울특별시", "경기 / 경기도"처럼 표기가 섞여 있어서,
// 검색어와 주소를 모두 짧은 표기로 바꾼 뒤 비교한다. (화면에 보이는 주소는 원본 그대로)
const REGION_ALIASES = [
  ["서울특별시", "서울"], ["서울시", "서울"],
  ["부산광역시", "부산"], ["대구광역시", "대구"], ["인천광역시", "인천"],
  ["광주광역시", "광주"], ["대전광역시", "대전"], ["대전시", "대전"],
  ["울산광역시", "울산"], ["울산시", "울산"], ["세종특별자치시", "세종"],
  ["경기도", "경기"], ["강원특별자치도", "강원"], ["강원도", "강원"],
  ["충청북도", "충북"], ["충청남도", "충남"],
  ["전북특별자치도", "전북"], ["전라북도", "전북"], ["전라남도", "전남"],
  ["경상북도", "경북"], ["경상남도", "경남"], ["제주특별자치도", "제주"],
];

// 검색어·매장 정보를 같은 규칙으로 바꿔 비교 (시·도 표기 통일 + 공백 제거)
export function normalizeForSearch(text) {
  let s = String(text || "");
  for (const [from, to] of REGION_ALIASES) {
    s = s.split(from).join(to);
  }
  return s.replace(/\s+/g, "").toLowerCase();
}

// 두 좌표 사이 직선거리(km, 하버사인 공식)
export function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function formatKm(km) {
  if (km == null || !Number.isFinite(km)) return "";
  return km < 10 ? `${km.toFixed(1)}km` : `${Math.round(km)}km`;
}

export function sortByNameKo(list) {
  return list.slice().sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), "ko", { numeric: true })
  );
}

function escapeHtml(str) {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// 네이버 지도 마커 HTML
// active: 선택한 매장 (파란 라벨 + 파란 핀) / highlighted: 방금 연 묶음의 매장 (라벨은 그대로, 핀만 파란색)
export function markerHtml(label, active, highlighted = false) {
  const icon = active || highlighted ? MARKER_ICON.active : MARKER_ICON.default;
  return (
    `<div class="marker"><div class="marker-txt${active ? " active" : ""}">${escapeHtml(label)}</div>` +
    `<img src="${icon}" alt="" style="margin:0;padding:0;border:0;display:block;max-width:none;max-height:none;-webkit-user-select:none;position:absolute;width:45px;height:45px;left:0;top:20px;"/>` +
    `</div>`
  );
}

// API 매장 데이터 → 화면용 (영업 중이고 좌표가 있는 매장만)
export function toStoreRow(store) {
  if (!store || String(store.end_ind).trim() !== "1") return null;
  const lat = Number(store.latitude);
  const lng = Number(store.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const addr = [store.addr1, store.addr2].filter(Boolean).join(" ");
  const tag = [];
  if (store.wifi_yn == "Y") tag.push("wifi");
  if (store.parking_yn == "Y") tag.push("parking");
  if (store.hearing_aid == "Y") tag.push("earaid");

  return {
    id: store.id,
    strCode: store.str_code,
    name: store.str_name,
    lat,
    lng,
    addr,
    tel: store.tel_no || "",
    tag,
    blogUrl: store.blog_add || "",
    kakaoUrl: store.kakao_add || "",
    naverUrl: store.naver_place_add || "",
    locationInfo: store.location_information || "",
    workHour: store.work_hour || "",
    imgSrc: (Array.isArray(store.store_image_masters) ? store.store_image_masters : [])
      .map((img) => img && img.store_photo_url)
      .filter((url) => typeof url === "string" && url.trim().length > 0)
      .map((url) => url.trim()),
    discount: Array.isArray(store.store_affiliate_masters) ? store.store_affiliate_masters : [],
    searchKey: normalizeForSearch(`${store.str_name || ""} ${addr}`),
  };
}

export function toStoreRows(resData) {
  const list = Array.isArray(resData)
    ? resData
    : resData && Array.isArray(resData.data)
      ? resData.data
      : [];
  return list.map(toStoreRow).filter(Boolean);
}

// 브라우저에 남아 있는 예전 형식 캐시(매장코드 없음)는 쓰지 않는다
export function isStoreRowList(list) {
  return (
    Array.isArray(list) &&
    list.length > 0 &&
    list.every((s) => s && s.strCode && typeof s.searchKey === "string")
  );
}

export function emptyDetailProps() {
  return {
    id: null, strCode: "", name: "", lat: null, lng: null, addr: "", tel: "", tag: [],
    blogUrl: "", kakaoUrl: "", naverUrl: "", locationInfo: "", workHour: "",
    imgSrc: [], discount: [],
  };
}

// 상세 패널(MDetailStore)에 넘길 순수 데이터
export function toDetailProps(item) {
  if (!item) return emptyDetailProps();
  return {
    id: item.id,
    strCode: item.strCode || "",
    name: item.name || "",
    lat: item.lat,
    lng: item.lng,
    addr: item.addr || "",
    tel: item.tel || "",
    tag: (item.tag || []).slice(),
    blogUrl: item.blogUrl || "",
    kakaoUrl: item.kakaoUrl || "",
    naverUrl: item.naverUrl || "",
    locationInfo: item.locationInfo || "",
    workHour: item.workHour || "",
    imgSrc: (item.imgSrc || []).slice(),
    discount: (item.discount || []).map((d) => ({ ...d })),
  };
}

// [상담예약] 링크: 고른 매장의 매장코드를 함께 넘긴다 (신청 화면에서 매장 칸 기본값으로 사용)
export function consultationLink(strCode) {
  return { path: "/customer/consultation", query: strCode ? { store: strCode } : {} };
}

// 매장 주소 복사 (요약 시트·상세 공통). clipboard API가 없으면(http 개발 서버·오래된 브라우저) 예전 방식으로
export function copyAddress(text) {
  if (!text) return;
  const done = () => alert("복사되었습니다, 감사합니다.");
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
  } else {
    fallbackCopy(text, done);
  }
}

function fallbackCopy(text, done) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
    done();
  } finally {
    document.body.removeChild(ta);
  }
}

// "내 위치" 표시 (매장 라벨과 같은 둥근 모양, 흰 바탕 + 파란 테두리 + 파란 점)
export const MY_LOCATION_ANCHOR = { x: 40, y: 37 }; // 파란 점 가운데
export function myLocationHtml() {
  return (
    `<div class="my-location">` +
    `<div class="my-location-txt">내 위치</div>` +
    `<div class="my-location-dot"></div>` +
    `</div>`
  );
}

// 위치 권한 상태: "granted" | "denied" | "prompt" | "unknown"(권한 조회를 지원하지 않는 브라우저)
export async function locationPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      return (await navigator.permissions.query({ name: "geolocation" })).state;
    }
  } catch (e) {
    /* 권한 조회를 지원하지 않는 브라우저 */
  }
  return "unknown";
}

// 현재 위치 한 번 받기 → { lat, lng }. 거부·시간 초과·미지원이면 에러(code)로 실패
export function currentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 0 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      reject,
      { timeout: 10000, maximumAge: 60000 }
    );
  });
}

// 위치를 못 받았을 때 안내 문구. 이미 거부된 경우 브라우저가 다시 묻지 않으므로 허용 방법을 안내한다
export function locationErrorMessage(err) {
  if (err && err.code === 0) {
    return "이 브라우저에서는 현재 위치를 확인할 수 없어요. 지역이나 매장명으로 검색해주세요.";
  }
  if (!err || err.code !== 1) {
    return "현재 위치를 확인하지 못했어요. 잠시 후 다시 눌러주세요.";
  }
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return "위치 권한이 꺼져 있어요.\n\n 웹 사이트 설정 → 위치를 '허용'으로 바꾼 뒤 다시 눌러주세요.";
    // (아이폰 설정 → 개인정보 보호 → 위치 서비스도 켜져 있어야 해요)
  }
  if (/Android/i.test(ua)) {
    return "위치 권한이 꺼져 있어요.\n\n주소창 왼쪽 아이콘 → 권한 → 위치를 '허용'으로 바꾼 뒤 다시 눌러주세요.";
  }
  return "위치 권한이 꺼져 있어요.\n\n주소창 왼쪽 아이콘 → 사이트 설정 → 위치를 '허용'으로 바꾼 뒤 다시 눌러주세요.";
}

// 로컬·사내망 미리보기인지 (운영 도메인에서는 테스트용 파라미터를 모두 무시)
export function isLocalPreviewHost() {
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    /^(127|10)\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
}

// 미리보기에서만 ?mockLat=&mockLng= 로 위치 허용 상태를 흉내 낸다
export function getMockLocation(query) {
  if (!isLocalPreviewHost() || !query || !query.mockLat || !query.mockLng) return null;
  const lat = Number(query.mockLat);
  const lng = Number(query.mockLng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}
